import React, { PropsWithChildren, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Image, Stack } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import "./WelcomeBonusFaqsDrawer.scss";
import BottomDrawer from "../../BottomDrawer/BottomDrawer";
import { GenericModalProps } from "../../../utils/constants/generic-types";
import PrimaryActionButton from "../../Buttons/PrimaryActionButton/PrimaryActionButton";
import SecondaryActionButton from "../../Buttons/SecondaryActionButton/SecondaryActionButton";
import { ROUTES } from "../../../utils/constants";
import ArrowRightIcon, {
  ArrowRightIconVariants,
} from "../../icons/ArrowRightIcon";
import { HowToGetHpr } from "../../dashboard-page/modals/HprInfoModal/HprInfoModal";
import useDisclosure from "../../../hooks/useDisclosure";
import { SignupBonusOptionProperty } from "../../../utils/analytics";
import useAnalytics from "../../../hooks/useAnalytics";
import ImageHyperCoinStack from "../../../images/bonus-hyper-coin-stack.svg";
import ImageDoubleHyperCoins from "../../../images/double-hyper-coins.png";
import LatestWinsSlider from "../LatestWinsSlider/LatestWinsSlider";

interface FaqItem {
  id: number;
  question: React.ReactNode;
  answer: string;
}

function useWelcomeBonusFaqs() {
  const { t } = useTranslation("common", { keyPrefix: "onboarding" });

  const welcomeBonusFaqs: FaqItem[] = [
    {
      id: 1,
      question: (
        <span>
          {t("what_are_the_gold_coins")}{" "}
          <Image
            src={ImageHyperCoinStack}
            alt="HPR"
            style={{ width: "1.6em" }}
          />
          ?
        </span>
      ),
      answer: t("the_gold_coins_are_real_money_rewards"),
    },
    {
      id: 2,
      question: t("why_do_i_need_to_top_up"),
      answer: t("top_up_to_get_hpr_or_claim_your_hundred_percent_bonus"),
    },
    {
      id: 3,
      question: t("how_can_i_win_real_money"),
      answer: t("compete_in_1v1_and_multiplayer_tournaments_to_win_real_money"),
    },
    {
      id: 4,
      question: t("how_do_i_withdraw_my_winnings"),
      answer: t("instantly_cash_out_winnings"),
    },
  ];

  return welcomeBonusFaqs;
}

interface WelcomeBonusFaqDetailsProps extends GenericModalProps {
  faq: FaqItem;
  showHowToGetHpr?: boolean;
}

function WelcomeBonusFaqDetails({
  isOpen,
  onClose,
  faq,
  showHowToGetHpr,
}: WelcomeBonusFaqDetailsProps) {
  const { t } = useTranslation("common");
  return (
    <BottomDrawer
      isOpen={isOpen}
      onClose={onClose}
      showCloseBtn={false}
      containerClass="faq-details-drawer"
      bodyClass="drawer-content"
      backdropClassName="faq-details-backdrop"
    >
      <Stack gap={4} className="text-content">
        <Stack gap={2}>
          <h2 className="faq-question">{faq.question}</h2>
          <p className="faq-answer">{faq.answer}</p>
        </Stack>

        {showHowToGetHpr && (
          <HowToGetHpr showSteps={[faq.id === 1, true, true]} />
        )}
      </Stack>

      <PrimaryActionButton size="lg" text={t("got_it")} onClick={onClose} />
    </BottomDrawer>
  );
}

function WelcomeBonusFaq({
  children,
  isSelected,
  handleClick,
}: PropsWithChildren<{ isSelected: boolean; handleClick: () => void }>) {
  return (
    <button
      className={`d-flex justify-content-between align-items-center gap-3 faq-item ${
        isSelected ? "selected" : ""
      }`}
      onClick={handleClick}
    >
      {children}
      <ArrowRightIcon stroke="#8C8C8C" variant={ArrowRightIconVariants.THIN} />
    </button>
  );
}

export default function WelcomeBonusFaqsDrawer({
  isOpen,
  onClose,
}: GenericModalProps) {
  const navigate = useNavigate();
  const { sendSignupBonusOptionsEvent } = useAnalytics();
  const { t } = useTranslation("common", { keyPrefix: "onboarding" });
  const [selectedFaqId, setSelectedFaqId] = useState<number>();
  const welcomeBonusFaqs = useWelcomeBonusFaqs();
  const selectedFaq = welcomeBonusFaqs.find((faq) => faq.id === selectedFaqId);
  const {
    isOpen: isFaqDetailsOpen,
    onOpen: openFaqDetails,
    onClose: closeFaqDetails,
  } = useDisclosure();

  const handleFaqClick = (faqId: number) => {
    setSelectedFaqId(faqId);
    setTimeout(() => {
      openFaqDetails();
    }, 500);
  };

  const handleCloseFaqDetails = () => {
    closeFaqDetails();
    setTimeout(() => {
      setSelectedFaqId(undefined);
    }, 300);
  };

  const handleClaimBonusLater = () => {
    sendSignupBonusOptionsEvent(SignupBonusOptionProperty.SKIP);
    navigate(ROUTES.ROOT.DASHBOARD);
  };

  return (
    <>
      <BottomDrawer
        isOpen={isOpen}
        onClose={onClose}
        containerClass="welcome-bonus-faqs-drawer"
        headingClass="p-3 welcome-bonus-faqs-heading"
        bodyClass="bg-white"
        heading={
          <Stack gap={4} className="justify-content-end h-100">
            <Image
              src={ImageDoubleHyperCoins}
              alt="Hyper coins"
              width={80}
              className="align-self-center"
            />
            <LatestWinsSlider />
          </Stack>
        }
      >
        <Stack gap={3} className="main-content">
          <Stack gap={2} className="header-text">
            <h2 className="main-copy">{t("not_sure_yet")}?</h2>
            <p className="sub-copy">
              {t("we_have_answered_some_burning_questions")}...
            </p>
          </Stack>
          <Stack gap={3} className="faqs-list">
            {welcomeBonusFaqs.map((faq) => (
              <WelcomeBonusFaq
                key={faq.id}
                isSelected={selectedFaqId === faq.id}
                handleClick={() => handleFaqClick(faq.id)}
              >
                {faq.question}
              </WelcomeBonusFaq>
            ))}
          </Stack>
          <Stack gap={3} className="cta-container">
            <PrimaryActionButton
              size="lg"
              text={t("claim_hundred_percent_bonus")}
              onClick={onClose}
            />
            <SecondaryActionButton
              text={t("claim_bonus_later")}
              className="claim-later-btn"
              onClick={handleClaimBonusLater}
            />
          </Stack>
        </Stack>
      </BottomDrawer>

      <WelcomeBonusFaqDetails
        isOpen={isFaqDetailsOpen}
        onClose={handleCloseFaqDetails}
        faq={selectedFaq || welcomeBonusFaqs[0]}
        showHowToGetHpr={selectedFaqId === 1 || selectedFaqId === 3}
      />
    </>
  );
}
